// src/ui/slider.ts
import type { Hsl } from '../types';

export type SliderChannel = 'h' | 's' | 'l';

const CHANNEL_LABEL: Record<SliderChannel, string> = {
  h: 'Hue',
  s: 'Saturation',
  l: 'Lightness',
};

const CHANNEL_MAX: Record<SliderChannel, number> = {
  h: 360,
  s: 100,
  l: 100,
};

const CHANNEL_SUFFIX: Record<SliderChannel, string> = {
  h: '°',
  s: '%',
  l: '%',
};

// Live-channel gradient for each track — the track previews the colors the
// thumb will produce at the current values of the other two channels.
function gradientForChannel(channel: SliderChannel, cur: Hsl): string {
  if (channel === 'h') {
    const stops = [0, 60, 120, 180, 240, 300, 360]
      .map(h => `hsl(${h}, ${cur.s}%, ${cur.l}%)`)
      .join(', ');
    return `linear-gradient(to right, ${stops})`;
  }
  if (channel === 's') {
    return `linear-gradient(to right, hsl(${cur.h}, 0%, ${cur.l}%), hsl(${cur.h}, 100%, ${cur.l}%))`;
  }
  // lightness: black → pure hue at 50% L → white
  return `linear-gradient(to right, #000, hsl(${cur.h}, ${cur.s}%, 50%), #fff)`;
}

export function renderSlider(channel: SliderChannel, current: Hsl): string {
  const label = CHANNEL_LABEL[channel];
  const max = CHANNEL_MAX[channel];
  const suffix = CHANNEL_SUFFIX[channel];
  const v = Math.round(current[channel]);
  const gradient = gradientForChannel(channel, current);
  return `
    <div class="slider-row" data-channel="${channel}">
      <div class="slider-label">
        <span>${label}</span>
        <span class="value">${v}${suffix}</span>
      </div>
      <div class="slider-controls">
        <button type="button" class="slider-step" data-step="-1" aria-label="Decrease ${label}">−</button>
        <input type="range" min="0" max="${max}" value="${v}" step="1" aria-label="${label}" style="background: ${gradient};" />
        <button type="button" class="slider-step" data-step="1" aria-label="Increase ${label}">+</button>
      </div>
    </div>
  `;
}

// Wire all three sliders at once. Tracks the current HSL so the gradient
// backgrounds on the *other* sliders update live as any channel changes,
// and so the ± step buttons can operate against the real current value.
// Returns a cleanup function that removes all listeners.
export function wireSliders(
  root: HTMLElement,
  initial: Hsl,
  onChange: (next: Hsl) => void
): () => void {
  let cur: Hsl = { ...initial };
  const rows = Array.from(root.querySelectorAll('.slider-row')) as HTMLElement[];
  const cleanups: Array<() => void> = [];

  const updateGradients = () => {
    for (const row of rows) {
      const ch = row.dataset.channel as SliderChannel;
      const input = row.querySelector('input[type=range]') as HTMLInputElement;
      input.style.background = gradientForChannel(ch, cur);
    }
  };

  for (const row of rows) {
    const ch = row.dataset.channel as SliderChannel;
    const input = row.querySelector('input[type=range]') as HTMLInputElement;
    const valueEl = row.querySelector('.value') as HTMLElement;
    const suffix = CHANNEL_SUFFIX[ch];
    const max = CHANNEL_MAX[ch];

    const apply = (raw: number) => {
      const v = Math.max(0, Math.min(max, Math.round(raw)));
      input.value = String(v);
      valueEl.textContent = `${v}${suffix}`;
      cur = ch === 'h' ? { ...cur, h: v } : ch === 's' ? { ...cur, s: v } : { ...cur, l: v };
      updateGradients();
      onChange(cur);
    };

    const inputHandler = () => apply(parseInt(input.value, 10));
    input.addEventListener('input', inputHandler);
    cleanups.push(() => input.removeEventListener('input', inputHandler));

    const stepBtns = Array.from(row.querySelectorAll('.slider-step')) as HTMLElement[];
    for (const btn of stepBtns) {
      const step = parseInt(btn.dataset.step ?? '0', 10);
      const stepHandler = (e: Event) => {
        e.preventDefault();
        apply(parseInt(input.value, 10) + step);
      };
      btn.addEventListener('click', stepHandler);
      cleanups.push(() => btn.removeEventListener('click', stepHandler));

      // Prevent iOS long-press callout by intercepting touch events.
      // touchstart preventDefault stops the callout but also suppresses click,
      // so we fire the step action on touchend instead.
      let touched = false;
      const onTouchStart = (e: Event) => {
        e.preventDefault();
        touched = true;
      };
      const onTouchEnd = () => {
        if (touched) {
          touched = false;
          apply(parseInt(input.value, 10) + step);
        }
      };
      const onTouchCancel = () => { touched = false; };
      btn.addEventListener('touchstart', onTouchStart, { passive: false });
      btn.addEventListener('touchend', onTouchEnd);
      btn.addEventListener('touchcancel', onTouchCancel);
      cleanups.push(() => {
        btn.removeEventListener('touchstart', onTouchStart);
        btn.removeEventListener('touchend', onTouchEnd);
        btn.removeEventListener('touchcancel', onTouchCancel);
      });

      const suppress = (e: Event) => e.preventDefault();
      btn.addEventListener('contextmenu', suppress);
      cleanups.push(() => btn.removeEventListener('contextmenu', suppress));
    }
  }

  return () => cleanups.forEach(c => c());
}
